import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactFlagsSelect from "react-flags-select";
import { Dialog, Transition, Listbox } from "@headlessui/react";
import WebsiteConfigForm from "../components/WebsiteConfigForm";
import Input from "../components/common/Input";
import { fetchConfigs, addConfig, updateConfig, deleteConfig, setFilters, setPage, setPageSize, logout } from "../store/websiteConfigSlice";

const FilterInput = ({ label, value, onChange }) => (
	<div className="relative">
		<Input label={label} showLabel={false} value={value} onChange={(e) => onChange(e.target.value)} type="search" placeholder={`Search by ${label.toLowerCase()}`} className="pr-8" />
		{/* {value && (
      <button
        onClick={() => onChange('')}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    )} */}
	</div>
);

const CountryFilterSelect = ({ label, value, onChange }) => (
	<div className="flex flex-col">
		{/* <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label> */}
		<div className="relative">
			<ReactFlagsSelect selected={value} onSelect={onChange} searchable placeholder="Select Country" className="!p-0" selectButtonClassName="!w-full !px-4 !py-2.5 !text-sm !border-0 !rounded-xl !shadow-[0_2px_8px_rgb(0,0,0,0.08)] focus:!shadow-[0_2px_8px_rgb(0,0,0,0.12)] !bg-white" />
			{value && (
				<button
					onClick={(e) => {
						e.preventDefault();
						onChange("");
					}}
					className="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
					<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			)}
		</div>
	</div>
);

const PageSizeSelector = ({ value, onChange }) => {
	const options = [10, 20, 50];

	return (
		<Listbox value={value} onChange={onChange}>
			<div className="relative">
				<Listbox.Button className="relative w-20 py-2 pl-3 pr-8 text-left bg-white border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
					<span className="block truncate">{value}</span>
					<span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
						<svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
						</svg>
					</span>
				</Listbox.Button>
				<Transition as={React.Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
					<Listbox.Options className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
						{options.map((option) => (
							<Listbox.Option
								key={option}
								value={option}
								className={({ active }) =>
									`${active ? "text-blue-900 bg-blue-100" : "text-gray-900"}
                  cursor-pointer select-none relative py-2 pl-3 pr-9`
								}>
								{({ selected, active }) => (
									<>
										<span className={`${selected ? "font-medium" : "font-normal"} block truncate`}>{option}</span>
										{selected && (
											<span
												className={`${active ? "text-blue-600" : "text-blue-600"}
                          absolute inset-y-0 right-0 flex items-center pr-4`}>
												<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											</span>
										)}
									</>
								)}
							</Listbox.Option>
						))}
					</Listbox.Options>
				</Transition>
			</div>
		</Listbox>
	);
};

const Pagination = ({ total, page, pageSize, onPageChange, onPageSizeChange }) => {
	const totalPages = Math.ceil(total / pageSize);

	return (
		<div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-white sm:px-6 space-y-4 sm:space-y-0">
			<div className="flex items-center space-x-2">
				<span className="text-sm text-gray-700">Show</span>
				<PageSizeSelector value={pageSize} onChange={onPageSizeChange} />
				<span className="text-sm text-gray-700">entries</span>
			</div>

			<div className="flex items-center">
				<span className="text-sm text-gray-700 mr-4">
					Page {page} of {totalPages} ({total} total)
				</span>
				<nav className="flex space-x-2">
					<button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
						Previous
					</button>
					<button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
						Next
					</button>
				</nav>
			</div>
		</div>
	);
};

// 详情 Modal 组件
const DetailsModal = ({ config, isOpen, onClose, onEdit, onDelete }) => (
	<Transition show={isOpen} as={React.Fragment}>
		<Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
			<div className="min-h-screen px-4 text-center">
				<Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
					<Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
				</Transition.Child>

				<span className="inline-block h-screen align-middle" aria-hidden="true">
					&#8203;
				</span>

				<Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
					<div className="inline-block w-full max-w-md p-6 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
						<div className="flex justify-between items-center mb-4">
							<Dialog.Title className="text-lg font-medium text-gray-900">Website Details</Dialog.Title>
							<button onClick={onClose} className="text-gray-400 hover:text-gray-500">
								<span className="sr-only">Close</span>
								<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						<div className="space-y-6">
							<div className="bg-gray-50 rounded-lg p-4">
								<h3 className="text-sm font-medium text-gray-500 mb-3">Basic Information</h3>
								<div className="space-y-2">
									<div>
										<span className="text-sm text-gray-500">Domain:</span>
										<p className="text-sm font-medium text-gray-900">{config.domain}</p>
									</div>
									<div>
										<span className="text-sm text-gray-500">Country:</span>
										<p className="text-sm font-medium text-gray-900">{config.country}</p>
									</div>
									<div>
										<span className="text-sm text-gray-500">Pixel:</span>
										<p className="text-sm font-medium text-gray-900">{config.pixel}</p>
									</div>
								</div>
							</div>

							<div className="bg-gray-50 rounded-lg p-4">
								<h3 className="text-sm font-medium text-gray-500 mb-3">Contact Information</h3>
								<div className="space-y-2">
									{config.whatsapp_phone && (
										<div>
											<span className="text-sm text-gray-500">Phone Groups:</span>
											<p className="text-sm font-medium text-gray-900">{config.whatsapp_phone}</p>
										</div>
									)}
									{config.telegram_group_id && (
										<div>
											<span className="text-sm text-gray-500">Remarks:</span>
											<p className="text-sm font-medium text-gray-900">{config.remarks}</p>
										</div>
									)}
								</div>
							</div>

							<div className="bg-gray-50 rounded-lg p-4">
								<h3 className="text-sm font-medium text-gray-500 mb-3">Operations</h3>
								<div className="flex space-x-4">
									<button
										onClick={() => {
											onClose();
											onEdit(config);
										}}
										className="flex items-center text-sm text-blue-600 hover:text-blue-800">
										<svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
										</svg>
										Edit Status
									</button>
									<button
										onClick={() => {
											onClose();
											onDelete(config.id);
										}}
										className="flex items-center text-sm text-red-600 hover:text-red-800">
										<svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
										</svg>
										Delete
									</button>
								</div>
							</div>
						</div>
					</div>
				</Transition.Child>
			</div>
		</Dialog>
	</Transition>
);

// 移动端的配置卡片组件
const ConfigCard = ({ config, onEdit, onDelete }) => {
	const [showDetails, setShowDetails] = useState(false);

	return (
		<>
			<div className="bg-white rounded-lg shadow-sm">
				<div className="flex items-center px-2 py-3">
					<div className="min-w-0 flex-1 grid grid-cols-2 gap-2">
						<div className="truncate">
							<p className="text-sm font-medium text-gray-900 text-center">{config.domain}</p>
						</div>
						<div className="truncate">
							<p className="text-sm text-gray-500 text-center">{config.whatsapp_phone || "-"}</p>
						</div>
					</div>
					<button onClick={() => setShowDetails(true)} className="ml-4 text-blue-600 p-1 hover:text-blue-800">
						<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
						</svg>
					</button>
				</div>
			</div>

			<DetailsModal config={config} isOpen={showDetails} onClose={() => setShowDetails(false)} onEdit={onEdit} onDelete={onDelete} />
		</>
	);
};

// PC 端表格组件
const DesktopTable = ({ configs, onEdit, onDelete }) => (
	<div className="hidden lg:block overflow-x-auto">
		<table className="min-w-full">
			<thead>
				<tr className="border-b">
					<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
					<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
					<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Groups</th>
					<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pixel</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
					<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
				</tr>
			</thead>
			<tbody className="bg-white divide-y divide-gray-200">
				{configs.map((config) => (
					<tr key={config.id} className="hover:bg-gray-50">
						<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.domain}</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{config.country || "-"}</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{config.whatsapp_phone || "-"}</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{config.pixel || "-"}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{config.remarks || "-"}</td>
						<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
							<button onClick={() => onEdit(config)} className="text-blue-600 hover:text-blue-800">
								Edit
							</button>
							<button onClick={() => onDelete(config.id)} className="text-red-600 hover:text-red-800">
								Delete
							</button>
						</td>
					</tr>
				))}
			</tbody>
		</table>
	</div>
);

// 移动端列表组件
const MobileList = ({ configs, onEdit, onDelete }) => (
	<div className="lg:hidden">
		{/* 移动端表头 */}
		<div className="px-4 py-2">
			<div className="grid grid-cols-2 gap-2">
				<div className="text-xs font-bold uppercase text-center">Domain</div>
				<div className="text-xs font-bold uppercase text-center">Phone Groups</div>
			</div>
		</div>

		{/* 列表内容 */}
		<div className="space-y-2">
			{configs.map((config) => (
				<ConfigCard key={config.id} config={config} onEdit={onEdit} onDelete={onDelete} />
			))}
		</div>
	</div>
);

const WebsiteConfigList = () => {
	const [showForm, setShowForm] = useState(false);
	const [editingConfig, setEditingConfig] = useState(null);
	const dispatch = useDispatch();
	const { configs, loading, pagination, filters } = useSelector((state) => state.websiteConfig);

	const fetchData = () => {
		dispatch(
			fetchConfigs({
				page: pagination.page,
				pageSize: pagination.pageSize,
				...filters,
			})
		);
	};

	useEffect(() => {
		fetchData();
	}, [pagination.page, pagination.pageSize, filters]);

	const handleSubmit = async (data) => {
		if (editingConfig) {
			await dispatch(updateConfig({ id: editingConfig.id, ...data }));
		} else {
			await dispatch(addConfig(data));
		}
		setShowForm(false);
		setEditingConfig(null);
		fetchData();
	};

	const handleEdit = (config) => {
		setEditingConfig(config);
		setShowForm(true);
	};

	const handleDelete = async (id) => {
		if (window.confirm("Are you sure you want to delete this configuration?")) {
			await dispatch(deleteConfig(id));
			fetchData();
		}
	};

	const handleFilterChange = (field) => (value) => {
		dispatch(setFilters({ [field]: value }));
	};

	if (loading && !configs.length) {
		return <div className="p-4">Loading...</div>;
	}

	if (showForm) {
		return (
			<WebsiteConfigForm
				initialValues={editingConfig || {}}
				onSubmit={handleSubmit}
				onCancel={() => {
					setShowForm(false);
					setEditingConfig(null);
				}}
			/>
		);
	}

	return (
		<div className="flex flex-col bg-gray-50">
			{/* 头部区域 */}
			<div className="p-0">
				<div className="flex justify-between items-center mb-4">
					<button onClick={() => setShowForm(true)} className="flex-1 mr-2 px-4 py-3 text-md font-medium text-white bg-brand-primary rounded-xl hover:bg-brand-primary-dark">
						Add New
					</button>
				</div>

				{/* Filters */}
				<div className="mt-4">
					<div className="flex flex-col lg:flex-row lg:space-x-4 space-y-4 lg:space-y-0">
						<div className="flex-1">
							<FilterInput label="Domain" value={filters.domain} onChange={handleFilterChange("domain")} />
						</div>
						<div className="flex-1">
							<CountryFilterSelect label="Country" value={filters.country} onChange={handleFilterChange("country")} />
						</div>
						<div className="flex-1">
							<FilterInput label="Phone Groups" value={filters.whatsapp_phone} onChange={handleFilterChange("whatsapp_phone")} />
						</div>
					</div>
				</div>
			</div>

			{/* 列表区域 */}
			<div className="flex flex-col min-h-0 bg-white rounded-[20px] mt-3">
				{/* 响应式列表/表格 */}
				<div className="flex-1 overflow-y-auto">
					<MobileList configs={configs} onEdit={handleEdit} onDelete={handleDelete} />
					<DesktopTable configs={configs} onEdit={handleEdit} onDelete={handleDelete} />
				</div>

				{/* 分页 */}
				<div className="bg-white px-4 py-3">
					<Pagination total={pagination.total} page={pagination.page} pageSize={pagination.pageSize} onPageChange={(page) => dispatch(setPage(page))} onPageSizeChange={(pageSize) => dispatch(setPageSize(pageSize))} />
				</div>
			</div>
		</div>
	);
};

export default WebsiteConfigList;
